import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Box, Flex } from "flicket-ui";
import { AnimatePresence } from "framer-motion";
import { useRouter } from "next/router";

import { ContentModal, Layout, Seo, Status } from "~components";
import HeaderAlert from "~components/common/common.HeaderAlert";
import {
  Actions,
  FilterModal,
  Filters,
  Header,
  MobileFilters,
  NonSeated,
  Options,
  SeatsIO,
} from "~components/reservation";
import { MobileOptions } from "~components/reservation/options/options.MobileLayout";
import { Placeholder } from "~components/reservation/options/options.Placeholder";
import { BaseContext } from "~context";
import { TicketDetailsModal } from "~features/seated-reservation/components/common/modal.TicketDetails";
import {
  IntegrationType,
  LandingPage,
  MembershipKind,
  VenueSeating,
} from "~graphql/sdk";
import { useAccount, useIsMobile, useIsMounted, useReservation } from "~hooks";
import { showToast } from "~lib";

const MembershipReservation = () => {
  const { organization } = useContext(BaseContext);
  const [isNavigating, setNavigating] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [onAddError, setOnAddError] = useState("");
  const isMobile = useIsMobile();
  const isMounted = useIsMounted();
  const router = useRouter();
  const [showOptions, setShowOptions] = useState(!router?.query?.ownedTickets);
  const { isAdmin } = useAccount();
  const [eventVenues, setEventVenues] = useState([]);
  const [locations, setLocations] = useState("");
  const {
    chart,
    membership,
    error,
    isLoading,
    handleOrderCreation,
    options,
    selectableZones,
    updateOptions,
    getPrice,
    changeTickets,
    isLoadingZones,
    handleGAOrderCreation,
    priceComp,
    seatAmountOptions,
  } = useReservation("membership", router.query.membershipId as string);

  useEffect(() => {
    router.prefetch("/checkout/[orderId]");
  }, []);

  const changeCoverType = (value: string) => updateOptions("coverType", value);
  const changeZoneType = (value: string) => updateOptions("zoneType", value);
  const changeSort = (value: string) => updateOptions("selectedSort", value);
  const setAmount = (value: number) => updateOptions("amount", value);
  const setZone = (value: string) => updateOptions("selectedZone", value);
  const setSection = (value: string) => updateOptions("selectedSection", value);
  const changeSeatSelect = (value: string) =>
    updateOptions("seatSelectType", value);

  useEffect(() => {
    if (
      !options?.validSeatSelection &&
      options?.amount >= 0 &&
      membership?.venue?.seating === VenueSeating.Seated
    ) {
      showToast(
        "Invalid seat selection. You have left a single seat empty",
        "error"
      );
    }
  }, [options?.validSeatSelection]);

  const closeOptions = useCallback(() => setShowOptions(false), [
    setShowOptions,
  ]);

  const onNavigateCheckout = useCallback(async () => {
    if (isNavigating) return;

    setNavigating(true);

    const wasSuccessful = await handleOrderCreation((errorMessage) => {
      setOnAddError(errorMessage);
      setIsErrorModalOpen(true);
      setNavigating(false);
      return;
    });

    if (!wasSuccessful) {
      setNavigating(false);
    }
  }, [handleOrderCreation]);

  const handleClose = () => {
    if (!options?.amount) {
      setAmount(3);
    }

    closeOptions();
  };

  useEffect(() => {
    membership?.events.forEach((membershipEvent) => {
      const eventVenueList = eventVenues;
      eventVenueList.push(membershipEvent.venue);

      const newlist = Array.from(
        new Set(eventVenueList.map((venue) => venue.name))
      ).map((name) => {
        return {
          name: name,
          address: eventVenueList.find((venue) => venue.name === name).address,
        };
      });
      setEventVenues(newlist);
      setLocations(
        newlist
          .map((venue) => `${venue.name}, ${venue?.address?.city}`)
          .join(", ")
      );
    });
  }, [membership]);

  const memoizedOptions = useMemo(
    () => (
      <AnimatePresence>
        <FilterModal
          isOpen={isMounted && showOptions && !isMobile}
          onClose={handleClose}
        >
          <Options
            amount={options?.amount}
            handleClose={handleClose}
            setAmount={setAmount}
            title={membership?.name}
            startDate={membership?.startDate}
            endDate={membership?.endDate}
            location={locations}
            seatAmountOptions={seatAmountOptions}
            filterOptions={membership?.venue?.filterOptions}
          />
        </FilterModal>
      </AnimatePresence>
    ),
    [
      options?.coverType,
      options?.zoneType,
      options?.selectedSort,
      options?.amount,
      closeOptions,
      changeCoverType,
      changeZoneType,
      changeSort,
      setAmount,
      isMobile,
      seatAmountOptions,
    ]
  );

  const laybuyGateway = membership?.gateways?.find(
    (gateway) => gateway.type === IntegrationType.PaymentLaybuy
  );

  //For seated memberships
  const memoizedActions = useMemo(
    () => (
      <Actions
        eventOrMembership={membership}
        type="membership"
        addons={membership?.addons}
        changeTickets={changeTickets}
        laybuyGateway={laybuyGateway}
        changingSeatsFee={membership?.changingSeatsFee}
        ticketTypes={membership?.types}
        venueName={membership?.venue?.name}
        zones={selectableZones}
        currentZone={options?.selectedZone}
        setCurrentZone={setZone}
        validSeatSelection={options?.validSeatSelection}
        currentSort={options?.selectedSort}
        currentSection={options?.selectedSection}
        setCurrentSection={setSection}
        navigateCheckout={onNavigateCheckout}
        isNavigating={isNavigating}
        getPrice={getPrice}
        priceComp={priceComp}
      />
    ),
    [
      membership?.addons,
      membership?.types,
      membership?.venue?.name,
      membership?.changingSeatsFee,
      changeTickets,
      selectableZones,
      setZone,
      setSection,
      options?.selectedSort,
      options?.selectedZone,
      options?.selectedSection,
      onNavigateCheckout,
      isNavigating,
      getPrice,
      priceComp,
      options?.validSeatSelection,
    ]
  );

  const memoizedSeatsIO = useMemo(
    () => <SeatsIO showOverlay={showOptions} chart={chart} />,
    [showOptions, chart]
  );

  return (
    <>
      <Seo title="Membership" description="Membership" />
      <Status error={error} loading={isLoading || !membership?.venue?.seating}>
        <Header
          type="membership"
          title={membership?.name}
          startDate={membership?.startDate}
          endDate={membership?.endDate}
          location={locations}
          accessibility={membership?.accessibility}
          image={membership?.banner}
          description={membership?.description}
          supportInfo={organization?.supportInfo}
          goBack={() =>
            void router.push(
              organization?.landingPage === LandingPage.SingleEvent
                ? "/"
                : "/memberships"
            )
          }
          coordinates={`${membership?.venue?.location?.lat},${membership?.venue?.location?.lng}`}
        />
        <Layout layoutType="sub">
          {isAdmin && (
            <HeaderAlert
              message="Back office order"
              fixed={membership?.venue?.seating !== VenueSeating.Seated}
            />
          )}
          {membership?.venue?.seating !== VenueSeating.Seated ? (
            <NonSeated
              addons={membership?.addons}
              eventOrMembership={membership}
              zones={selectableZones}
              createOrder={handleGAOrderCreation}
              isMembership
              isAdmin={isAdmin}
              locations={locations}
              isDigital={membership?.membershipKind === MembershipKind.Digital}
            />
          ) : (
            <>
              <Filters
                amount={options?.amount}
                setAmount={setAmount}
                amountDisabled={!!changeTickets?.length}
                coverType={options?.coverType}
                setCoverType={changeCoverType}
                alcoholFreeZone={options?.zoneType}
                setAlcoholFreeZone={changeZoneType}
                selectedSort={options?.selectedSort}
                setSelectedSort={changeSort}
                seatSelectType={options?.seatSelectType}
                setSeatSelectType={changeSeatSelect}
                seatAmountOptions={seatAmountOptions}
                filterOptions={membership?.venue?.filterOptions}
              />
              <Flex
                justifyContent={["flex-end", "flex-start"]}
                overflow="hidden"
                position="relative"
                height={
                  showOptions
                    ? {
                        _: isAdmin
                          ? "calc(100vh - 96px)"
                          : "calc(100vh - 64px)",
                      }
                    : {
                        _: isAdmin
                          ? "calc(100vh - 96px)"
                          : "calc(100vh - 64px)",
                        md: isAdmin
                          ? "calc(100vh - 169px)"
                          : "calc(100vh - 122px)",
                      }
                }
                maxHeight={
                  showOptions
                    ? {
                        _: isAdmin
                          ? "calc(100vh - 96px)"
                          : "calc(100vh - 64px)",
                      }
                    : {
                        _: isAdmin
                          ? "calc(100vh - 96px)"
                          : "calc(100vh - 64px)",
                        md: isAdmin
                          ? "calc(100vh - 169px)"
                          : "calc(100vh - 122px)",
                      }
                }
              >
                <Flex w="100%" maxW="100%">
                  {!isMobile && (
                    <>
                      <Box
                        position="relative"
                        d={{ _: "none", md: "block" }}
                        width="100%"
                        minW={{ _: "100%", md: "256px" }}
                        maxW={{ _: "100%", md: "403px" }}
                      >
                        {isLoadingZones ||
                        typeof selectableZones === "undefined" ? (
                          <Placeholder />
                        ) : (
                          memoizedActions
                        )}
                      </Box>
                      {showOptions && memoizedOptions}
                    </>
                  )}
                  <Box position="relative" maxW="100%" width="100%" bg="N100">
                    {memoizedSeatsIO}
                    <MobileFilters
                      amount={options?.amount}
                      setAmount={setAmount}
                      amountDisabled={!!changeTickets?.length}
                      coverType={options?.coverType}
                      setCoverType={changeCoverType}
                      alcoholFreeZone={options?.zoneType}
                      setAlcoholFreeZone={changeZoneType}
                      selectedSort={options?.selectedSort}
                      setSelectedSort={changeSort}
                      seatSelectType={options?.seatSelectType}
                      setSeatSelectType={changeSeatSelect}
                      seatAmountOptions={seatAmountOptions}
                      filterOptions={membership?.venue?.filterOptions}
                    />
                    {isLoadingZones ||
                    typeof selectableZones === "undefined" ? (
                      <Placeholder />
                    ) : (
                      memoizedActions
                    )}
                  </Box>
                </Flex>
                <MobileOptions showOptions={showOptions}>
                  <Options
                    amount={options?.amount}
                    handleClose={handleClose}
                    setAmount={setAmount}
                    seatAmountOptions={seatAmountOptions}
                    filterOptions={membership?.venue?.filterOptions}
                  />
                </MobileOptions>
              </Flex>
            </>
          )}
          <TicketDetailsModal />
        </Layout>

        <ContentModal
          isOpen={isErrorModalOpen}
          setIsOpen={() => setIsErrorModalOpen(false)}
          title="There was a problem..."
        >
          <div dangerouslySetInnerHTML={{ __html: onAddError }}></div>
        </ContentModal>
      </Status>
    </>
  );
};

export default MembershipReservation;
